import { useState } from 'react';
import PayPalCheckoutModal from './PayPalCheckoutModal';

type BillingCycle = 'monthly' | 'annual' | 'lifetime';

export default function MembershipTiers() {
  const [billing, setBilling] = useState<BillingCycle>('monthly');
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<{ name: string; price: string; cycle: string } | null>(null);

  const tiers = [
    {
      name: 'Single Grade Level',
      description: 'Perfect for focused learning at one grade level',
      prices: { monthly: '$19', annual: '$149', lifetime: '$249' },
      savings: { annual: 'Save $79/yr vs monthly', lifetime: 'Best for long-term' },
      features: [
        'Complete curriculum for one grade (K-12)',
        'All video lessons for selected grade',
        'Downloadable worksheets & practice tests',
        'Digital textbook access',
        'Question & feedback system',
        'Regular content updates'
      ],
      icon: 'ri-book-line',
      popular: false,
      gradient: 'from-teal-500 to-emerald-600',
      badge: null,
      included: {
        content: [
          { item: 'Video Lessons', count: 80, max: 100 },
          { item: 'Practice Worksheets', count: 150, max: 200 },
          { item: 'Practice Tests', count: 12, max: 15 },
          { item: 'Digital Textbook Chapters', count: 1, max: 1 }
        ],
        support: [
          { item: 'Email Support', available: true },
          { item: 'Response Time', detail: '48 hours' },
          { item: 'Live Chat', available: false },
          { item: 'Phone Support', available: false }
        ],
        extras: [
          { item: 'Progress Tracking', available: true },
          { item: 'Downloadable Resources', available: true },
          { item: 'Mobile Access', available: true },
          { item: 'Offline Mode', available: false },
          { item: 'Certificate of Completion', available: false }
        ]
      }
    },
    {
      name: 'K-5 Bundle',
      description: 'Complete elementary mathematics mastery',
      prices: { monthly: '$39', annual: '$349', lifetime: '$499' },
      savings: { annual: 'Save $119/yr vs monthly', lifetime: 'Best for long-term' },
      features: [
        'All K-5 grade level content',
        '500+ video lessons',
        '1,000+ downloadable worksheets',
        'Complete digital textbook library',
        'Priority question & feedback support',
        'Free updates & new content',
        'Progress tracking tools'
      ],
      icon: 'ri-graduation-cap-line',
      popular: true,
      gradient: 'from-emerald-500 to-teal-600',
      badge: 'Most Popular',
      included: {
        content: [
          { item: 'Video Lessons', count: 500, max: 500 },
          { item: 'Practice Worksheets', count: 1000, max: 1000 },
          { item: 'Practice Tests', count: 60, max: 60 },
          { item: 'Digital Textbook Chapters', count: 6, max: 6 }
        ],
        support: [
          { item: 'Email Support', available: true },
          { item: 'Response Time', detail: '24 hours' },
          { item: 'Live Chat', available: true },
          { item: 'Phone Support', available: false }
        ],
        extras: [
          { item: 'Progress Tracking', available: true },
          { item: 'Downloadable Resources', available: true },
          { item: 'Mobile Access', available: true },
          { item: 'Offline Mode', available: true },
          { item: 'Certificate of Completion', available: true },
          { item: 'Parent Dashboard', available: true }
        ]
      }
    },
    {
      name: '6-12 Bundle',
      description: 'Middle & high school mathematics excellence',
      prices: { monthly: '$49', annual: '$449', lifetime: '$699' },
      savings: { annual: 'Save $139/yr vs monthly', lifetime: 'Best for long-term' },
      features: [
        'All 6-12 grade level content',
        '800+ advanced video lessons',
        '1,500+ worksheets & practice problems',
        'Complete algebra, geometry & calculus',
        'Test prep integration (SAT, ACT)',
        'Priority support & feedback',
        'College prep resources'
      ],
      icon: 'ri-calculator-line',
      popular: false,
      gradient: 'from-amber-500 to-orange-600',
      badge: null,
      included: {
        content: [
          { item: 'Video Lessons', count: 800, max: 800 },
          { item: 'Practice Worksheets', count: 1500, max: 1500 },
          { item: 'Practice Tests', count: 90, max: 90 },
          { item: 'Digital Textbook Chapters', count: 7, max: 7 },
          { item: 'SAT/ACT Prep Modules', count: 12, max: 12 }
        ],
        support: [
          { item: 'Email Support', available: true },
          { item: 'Response Time', detail: '12 hours' },
          { item: 'Live Chat', available: true },
          { item: 'Phone Support', available: true }
        ],
        extras: [
          { item: 'Progress Tracking', available: true },
          { item: 'Downloadable Resources', available: true },
          { item: 'Mobile Access', available: true },
          { item: 'Offline Mode', available: true },
          { item: 'Certificate of Completion', available: true },
          { item: 'College Prep Resources', available: true },
          { item: 'Study Planner', available: true }
        ]
      }
    },
    {
      name: 'Complete Access',
      description: 'Ultimate mathematics learning package',
      prices: { monthly: '$79', annual: '$699', lifetime: '$999' },
      savings: { annual: 'Save $249/yr vs monthly', lifetime: 'Best for long-term' },
      features: [
        'ALL K-12 grade levels',
        'ALL standardized test prep materials',
        'ALL original mathematics books',
        '1,500+ comprehensive video lessons',
        '3,000+ worksheets & practice tests',
        'VIP priority support',
        'One-on-one consultation session',
        'Exclusive advanced materials'
      ],
      icon: 'ri-vip-crown-line',
      popular: false,
      gradient: 'from-rose-500 to-pink-600',
      badge: 'Best Value',
      included: {
        content: [
          { item: 'Video Lessons', count: 1500, max: 1500 },
          { item: 'Practice Worksheets', count: 3000, max: 3000 },
          { item: 'Practice Tests', count: 180, max: 180 },
          { item: 'Digital Textbook Chapters', count: 13, max: 13 },
          { item: 'SAT/ACT/AP Prep Modules', count: 25, max: 25 },
          { item: 'Original Math Books', count: 8, max: 8 }
        ],
        support: [
          { item: 'Email Support', available: true },
          { item: 'Response Time', detail: 'Priority - 6 hours' },
          { item: 'Live Chat', available: true },
          { item: 'Phone Support', available: true },
          { item: 'One-on-One Consultation', detail: '1 session included' }
        ],
        extras: [
          { item: 'Progress Tracking', available: true },
          { item: 'Downloadable Resources', available: true },
          { item: 'Mobile Access', available: true },
          { item: 'Offline Mode', available: true },
          { item: 'Certificate of Completion', available: true },
          { item: 'College Prep Resources', available: true },
          { item: 'Study Planner', available: true },
          { item: 'VIP Community Access', available: true },
          { item: 'Exclusive Advanced Materials', available: true }
        ]
      }
    }
  ];

  const billingLabels: Record<BillingCycle, string> = {
    monthly: 'Monthly',
    annual: 'Annual',
    lifetime: 'Lifetime'
  };

  const billingSubLabels: Record<BillingCycle, string> = {
    monthly: 'per month',
    annual: 'per year',
    lifetime: 'one-time'
  };

  const billingFullLabels: Record<BillingCycle, string> = {
    monthly: 'Monthly',
    annual: 'Annual',
    lifetime: 'Lifetime'
  };

  const handleGetStarted = (tierName: string, price: string, cycle: BillingCycle) => {
    setSelectedTier({ name: tierName, price, cycle: billingFullLabels[cycle] });
    setModalOpen(true);
  };

  return (
    <section id="membership-tiers" className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
            Membership Tiers
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Choose the perfect plan for your learning journey. All memberships include full access to your chosen tier.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-gray-100 rounded-full p-1 gap-1">
            {(['monthly', 'annual', 'lifetime'] as BillingCycle[]).map((cycle) => (
              <button
                key={cycle}
                onClick={() => setBilling(cycle)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  billing === cycle
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {billingLabels[cycle]}
                {cycle === 'annual' && (
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${billing === 'annual' ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                    Save up to 37%
                  </span>
                )}
                {cycle === 'lifetime' && (
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${billing === 'lifetime' ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'}`}>
                    Best Deal
                  </span>
                )}
              </button>
            ))}
          </div>

          {billing === 'annual' && (
            <p className="mt-4 text-emerald-600 font-medium text-sm">
              🎉 Annual plans billed once per year — significant savings vs monthly
            </p>
          )}
          {billing === 'lifetime' && (
            <p className="mt-4 text-amber-600 font-medium text-sm">
              ⭐ Lifetime access — pay once, learn forever. No renewals ever.
            </p>
          )}
          {billing === 'monthly' && (
            <p className="mt-4 text-gray-500 font-medium text-sm">
              💡 Switch to Annual or Lifetime to unlock bigger savings
            </p>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-8">
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden group ${
                tier.popular ? 'ring-4 ring-emerald-500 scale-105' : ''
              }`}
            >
              {/* Badge */}
              {tier.badge && (
                <div className={`absolute top-0 right-0 bg-gradient-to-r ${tier.gradient} text-white px-5 py-2 rounded-bl-2xl font-semibold text-xs whitespace-nowrap`}>
                  {tier.badge}
                </div>
              )}

              <div className="p-7">
                {/* Icon */}
                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${tier.gradient} text-white mb-5 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${tier.icon} text-2xl`}></i>
                </div>

                {/* Tier Name */}
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {tier.name}
                </h3>

                {/* Price */}
                <div className="mb-1">
                  <span className="text-4xl font-bold text-gray-900">{tier.prices[billing]}</span>
                  <span className="text-sm text-gray-500 ml-2">{billingSubLabels[billing]}</span>
                </div>

                {/* Savings tag */}
                {billing !== 'monthly' && (
                  <div className="mb-4">
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                      {tier.savings[billing]}
                    </span>
                  </div>
                )}
                {billing === 'monthly' && <div className="mb-4" />}

                {/* Description */}
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                  {tier.description}
                </p>

                {/* CTA Button — opens PayPal modal */}
                <button
                  onClick={() => handleGetStarted(tier.name, tier.prices[billing], billing)}
                  className={`w-full py-3.5 bg-gradient-to-r ${tier.gradient} text-white font-semibold rounded-lg hover:opacity-90 transition-all duration-300 mb-6 whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2`}
                >
                  <i className="ri-paypal-line text-base"></i>
                  Get Started Now
                </button>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  {tier.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-start gap-3">
                      <i className="ri-check-line text-lg text-emerald-600 flex-shrink-0 mt-0.5"></i>
                      <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* What's Included Toggle */}
                <button
                  onClick={() => setExpandedCard(expandedCard === index ? null : index)}
                  className="w-full flex items-center justify-between py-3 px-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 cursor-pointer"
                >
                  <span className="text-sm font-semibold text-gray-900">What's Included</span>
                  <i className={`ri-arrow-${expandedCard === index ? 'up' : 'down'}-s-line text-lg text-gray-600 transition-transform duration-300`}></i>
                </button>

                {/* Expanded Content */}
                <div
                  className={`overflow-hidden transition-all duration-500 ${
                    expandedCard === index ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0'
                  }`}
                >
                  {/* Content Section */}
                  <div className="mb-6">
                    <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <i className="ri-play-circle-line text-emerald-600"></i>
                      Content Library
                    </h4>
                    <div className="space-y-3">
                      {tier.included.content.map((item, idx) => (
                        <div key={idx}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-700">{item.item}</span>
                            <span className="text-xs font-bold text-emerald-600">{item.count}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full bg-gradient-to-r ${tier.gradient}`}
                              style={{ width: `${(item.count / item.max) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Support Section */}
                  <div className="mb-6">
                    <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <i className="ri-customer-service-line text-emerald-600"></i>
                      Support &amp; Help
                    </h4>
                    <div className="space-y-2">
                      {tier.included.support.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-xs text-gray-700">{item.item}</span>
                          {item.available !== undefined ? (
                            item.available ? (
                              <i className="ri-check-line text-base text-emerald-600"></i>
                            ) : (
                              <i className="ri-close-line text-base text-gray-400"></i>
                            )
                          ) : (
                            <span className="text-xs font-medium text-emerald-600">{item.detail}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Extras Section */}
                  <div>
                    <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <i className="ri-star-line text-emerald-600"></i>
                      Extra Features
                    </h4>
                    <div className="space-y-2">
                      {tier.included.extras.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-xs text-gray-700">{item.item}</span>
                          {item.available ? (
                            <i className="ri-check-line text-base text-emerald-600"></i>
                          ) : (
                            <i className="ri-close-line text-base text-gray-400"></i>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col md:flex-row items-center gap-8 bg-gray-50 px-10 py-8 rounded-2xl">
            <div className="flex items-center gap-3">
              <i className="ri-shield-check-line text-4xl text-emerald-600"></i>
              <span className="text-lg text-gray-700">30-Day Money-Back Guarantee</span>
            </div>
            <div className="flex items-center gap-3">
              <i className="ri-lock-line text-4xl text-emerald-600"></i>
              <span className="text-lg text-gray-700">Secure Payment Processing</span>
            </div>
            <div className="flex items-center gap-3">
              <i className="ri-customer-service-2-line text-4xl text-emerald-600"></i>
              <span className="text-lg text-gray-700">24/7 Support Available</span>
            </div>
          </div>
        </div>
      </div>

      {/* PayPal Checkout Modal */}
      {selectedTier && (
        <PayPalCheckoutModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          tierName={selectedTier.name}
          billingCycle={selectedTier.cycle}
          price={selectedTier.price}
        />
      )}
    </section>
  );
}